import { z } from 'zod'
import { useApiMutation } from '../../utils/useApi'

// ============================================
// POST /image-request/request_images
// ============================================
// Mirrors the backend `TriggerImageRequestV010`. The request always carries a `lot`, a
// `detection_selector` (exactly one selection shape, discriminated by `type`), and a
// non-empty `image_types` list. The dashboard sends a `detection_uuids` selector when rows
// are selected, otherwise a `timerange` over the active filter window. Image delivery is
// async; the endpoint only acknowledges the queued request.

const ImageTypeSchema = z.enum(['render', 'lp', 'roi'])
export type ImageType = z.infer<typeof ImageTypeSchema>

// Every image artifact the backend can fetch.
export const ALL_IMAGE_TYPES: ImageType[] = ['render', 'roi', 'lp']

// Pick exactly one selection shape. Time ranges must be timezone-aware ISO-8601 strings
// (`upper` strictly after `lower`).
const DetectionSelectorSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('detection_uuids'),
        detection_uuids: z.array(z.string()).min(1)
    }),
    z.object({
        type: z.literal('timerange'),
        lower: z.string(),
        upper: z.string()
    })
])
export type DetectionSelector = z.infer<typeof DetectionSelectorSchema>

const RequestImagesRequestSchema = z.object({
    lot: z.string(),
    detection_selector: DetectionSelectorSchema,
    image_types: z.array(ImageTypeSchema).min(1)
})
export type RequestImagesRequest = z.infer<typeof RequestImagesRequestSchema>

// 200 acknowledges the queued request with the id of the created image request; we validate
// the shape but surface nothing from it.
const RequestImagesResponseSchema = z.object({ image_request_id: z.string() })
export type RequestImagesResponse = z.infer<typeof RequestImagesResponseSchema>

export { ImageTypeSchema, DetectionSelectorSchema, RequestImagesRequestSchema, RequestImagesResponseSchema }

export const useRequestImagesMutation = () =>
    useApiMutation<RequestImagesRequest, typeof RequestImagesResponseSchema>(
        '/image-request/request_images',
        RequestImagesResponseSchema
    )
